import { Button, ConfirmationDialog, cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CaretDownIcon,
  CaretRightIcon,
  PlusIcon,
  SidebarSimpleIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ReactNode, type Ref, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass } from "@/lib/utils/cell-input";
import {
  stickyHeadFirstRowClass,
  stickyTitleClass,
} from "@/lib/utils/sticky-table-header";

// Contracts panel shared by screens 909 (donneurs d'ordre) and 910 (travaux):
// slide-over, groupements, fixed top bar with the draft actions, scroll area,
// table titles and header cells.

/**
 * Header cells are pinned one by one (sticky-table-header): opaque background,
 * and the cell draws its own line. A collapsed border stays with the table and
 * leaves a gap in the pinned cell, and box-shadow doesn't apply to the cells of
 * a collapsed table.
 */
export const contractsHeadCellClass =
  "truncate bg-zinc-50 p-1.5 text-left font-medium after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-zinc-200 dark:bg-zinc-900 dark:after:bg-zinc-800";
/** First header row: also draws the top line of the frame. */
export const contractsHeadFirstRowCellClass = cn(
  stickyHeadFirstRowClass,
  contractsHeadCellClass,
  "before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-zinc-200 dark:before:bg-zinc-800",
);
/**
 * First-row cell with a second row under it: below its line, the pseudo-element
 * runs on in the cell's colour, under that row, which paints later. At a
 * fractional zoom the two pinned rows can sit a device pixel apart, and what
 * scrolls showed through. `truncate` would clip it.
 */
export const contractsHeadGroupCellClass = cn(
  contractsHeadFirstRowCellClass,
  "overflow-visible! after:bottom-auto after:top-[calc(100%-1px)] after:h-1 after:bg-[linear-gradient(var(--color-zinc-200)_1px,var(--color-zinc-50)_1px)] dark:after:bg-[linear-gradient(var(--color-zinc-800)_1px,var(--color-zinc-900)_1px)]",
);
/** Vertical border between the contract columns and the team columns (PO, N59/N65). */
export const contractsTeamsSeparatorClass =
  "border-l-2 border-zinc-300 dark:border-zinc-700";
/**
 * Frame of a contracts table, without a top border: it scrolled away and the
 * pinned header rose by its width on the first scroll.
 */
export const contractsTableFrameClass =
  "overflow-clip rounded-lg border border-t-0 border-zinc-200 dark:border-zinc-800";
/** Section of the screen's main grid; the gutter keeps the "Contrats" tab off its last column. */
export const contractsGridSectionClass =
  "flex h-full min-w-0 flex-1 flex-col pr-8";

/**
 * Ref that scrolls an element into its scroll area when it appears: a row added
 * at the end of a long table, the groupements opened above it. Stable, so React
 * only calls it on mount.
 */
export const revealOnMount = (element: HTMLElement | null) => {
  element?.scrollIntoView({ block: "nearest" });
};

/**
 * A panel refuses to close on its draft. The fixed id keeps a single toast: a
 * click outside a drawer asks to close twice, on pointer down then on focus.
 */
export const warnUnsavedDraft = (description: string) =>
  toaster.error({
    id: "unsaved-draft",
    title: "Modifications non enregistrées",
    description,
  });

export type ContractsSlideOverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** An unsaved draft blocks closing: the "Enregistrer" button would be out of sight. */
  isDirty: boolean;
  children: ReactNode;
};

/**
 * Overlay panel sliding over the screen's main grid, opened and closed by a
 * vertical "Contrats" tab. Render it inside the `relative` container that
 * holds the grid.
 */
export function ContractsSlideOver({
  open,
  onOpenChange,
  isDirty,
  children,
}: ContractsSlideOverProps) {
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (open && isDirty) {
            warnUnsavedDraft(
              "Enregistrez ou annulez les modifications des contrats avant de fermer ce panneau.",
            );
            return;
          }
          onOpenChange(!open);
        }}
        aria-label={open ? "Masquer les contrats" : "Afficher les contrats"}
        className={cn(
          "absolute top-1/2 z-50 flex -translate-y-1/2 flex-col items-center gap-1.5 rounded-l-lg border border-r-0 border-zinc-200 bg-white px-1.5 py-3 text-zinc-600 shadow-sm transition-[right] duration-300 ease-in-out hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800",
          open ? "right-[1180px]" : "right-0",
        )}
      >
        <SidebarSimpleIcon className="size-4" />
        <span
          className="text-xs font-medium"
          style={{ writingMode: "vertical-rl" }}
        >
          Contrats
        </span>
      </button>
      <aside
        className={cn(
          "absolute inset-y-0 right-0 z-40 flex w-[1180px] max-w-full flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white p-4 transition-transform duration-300 ease-in-out dark:border-zinc-800 dark:bg-zinc-950",
          open ? "translate-x-0 shadow-xl" : "translate-x-full",
        )}
      >
        {children}
      </aside>
    </>
  );
}

export type ContractGroup = {
  id: number;
  name: string;
  shortName: string | null;
  mandataireContractId: number | null;
};

export type ContractGroupsToolbarProps = {
  count: number;
  expanded: boolean;
  onToggle: () => void;
  onCreate: () => void;
};

/** "Groupements" toggle and "Créer un groupement", in the panel's fixed bar. */
export function ContractGroupsToolbar({
  count,
  expanded,
  onToggle,
  onCreate,
}: ContractGroupsToolbarProps) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100"
        aria-expanded={expanded}
      >
        {expanded ? (
          <CaretDownIcon className="size-4" />
        ) : (
          <CaretRightIcon className="size-4" />
        )}
        Groupements
        {count > 0 && (
          <span className="font-normal text-zinc-500">({count})</span>
        )}
      </button>
      <Button outline onClick={onCreate}>
        <PlusIcon data-slot="icon" className="size-4" />
        Créer un groupement
      </Button>
    </>
  );
}

type GroupDraft = {
  name: string;
  shortName: string;
  mandataireContractId: number | null;
};

const serverGroupDraft = (group: ContractGroup): GroupDraft => ({
  name: group.name,
  shortName: group.shortName ?? "",
  mandataireContractId: group.mandataireContractId,
});

/** "<what> : <the API's reason>", one per change the API refused. */
export const refusal = (label: string, error: { error?: string }) =>
  `${label} : ${error.error || "erreur inattendue, réessayez"}`;

/**
 * Draft of the groupements' name, abbreviation and mandataire, saved with
 * the contracts panel's draft (909 and 910). Creating and deleting a group
 * stay immediate.
 */
export function useContractGroupsDraft(groups: ContractGroup[]) {
  const [drafts, setDrafts] = useState<Map<number, GroupDraft>>(new Map());

  const change = (groupId: number, patch: Partial<GroupDraft>) => {
    const group = groups.find((g) => g.id === groupId);
    if (isNil(group)) return;
    setDrafts((prev) => {
      const server = serverGroupDraft(group);
      const next = { ...(prev.get(groupId) ?? server), ...patch };
      const updated = new Map(prev);
      const sameAsServer =
        next.name === server.name &&
        next.shortName === server.shortName &&
        next.mandataireContractId === server.mandataireContractId;
      if (sameAsServer) updated.delete(groupId);
      else updated.set(groupId, next);
      return updated;
    });
  };

  /**
   * Call after saving the contracts: a mandataire must already be in its
   * group. Returns the refused changes.
   */
  const save = async (): Promise<string[]> => {
    const refused: string[] = [];
    for (const group of groups) {
      const draft = drafts.get(group.id);
      if (isNil(draft)) continue;
      const server = serverGroupDraft(group);
      const label = `Groupement ${draft.name.trim() || group.name}`;
      if (draft.name !== server.name || draft.shortName !== server.shortName) {
        const { error } = await apiClient.PATCH(
          "/api/admin/yards/contract-groups/{id}",
          {
            params: { path: { id: group.id } },
            body: {
              name: draft.name.trim(),
              shortName: draft.shortName.trim() ? draft.shortName.trim() : null,
            },
          },
        );
        if (error) refused.push(refusal(label, error));
      }
      if (draft.mandataireContractId !== server.mandataireContractId) {
        const { error } = await apiClient.PATCH(
          "/api/admin/yards/contract-groups/{id}/mandataire",
          {
            params: { path: { id: group.id } },
            body: { contractId: draft.mandataireContractId },
          },
        );
        if (error) refused.push(refusal(label, error));
      }
    }
    return refused;
  };

  return {
    /** The groups with their draft values. */
    groups: groups.map((group) => {
      const draft = drafts.get(group.id);
      return isNil(draft) ? group : { ...group, ...draft };
    }),
    isDirty: drafts.size > 0,
    isGroupDirty: (group: ContractGroup) => drafts.has(group.id),
    hasUnnamedGroup: [...drafts.values()].some((d) => d.name.trim() === ""),
    change,
    reset: () => setDrafts(new Map()),
    save,
  };
}

/**
 * Drops the draft when a refetch brings a new baseline. The panel stays
 * mounted, rather than remounting on a `key`, so its table keeps its scroll
 * position after an action (screen-design.md).
 */
export function useDraftBaseline<T>(baseline: T, resetDraft: () => void) {
  const [current, setCurrent] = useState(baseline);
  if (current !== baseline) {
    setCurrent(baseline);
    resetDraft();
  }
}

/** Outcome of saving a contracts panel's draft. */
export function notifyDraftSaved(refused: string[]) {
  if (refused.length === 0) {
    toaster.success({
      title: "Succès",
      description: "Modifications enregistrées",
    });
    return;
  }
  toaster.error({
    title: "Modifications refusées",
    description: refused.join("\n"),
  });
}

export type ContractGroupsListProps = {
  /** The groups with their draft values. */
  groups: ContractGroup[];
  /** Contracts of the group, the ones that can be its mandataire. */
  candidatesFor: (group: ContractGroup) => { id: number; name: string }[];
  onRename: (
    group: ContractGroup,
    patch: { name?: string; shortName?: string },
  ) => void;
  /** Highlights a group changed in the draft. */
  isDirty: (group: ContractGroup) => boolean;
  onDelete: (group: ContractGroup) => void;
  onSetMandataire: (groupId: number, contractId: number | null) => void;
  /** Says which groups the screen leaves out, and why. */
  note?: string;
};

/** The expanded groupements, at the top of the panel's scroll area. */
export function ContractGroupsList({
  groups,
  candidatesFor,
  onRename,
  isDirty,
  onDelete,
  onSetMandataire,
  note,
}: ContractGroupsListProps) {
  return (
    <div ref={revealOnMount} className="space-y-2">
      {groups.length === 0 ? (
        <p className="text-sm text-zinc-400">Aucun groupement</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((group) => {
            const candidates = candidatesFor(group);
            const dirty = isDirty(group);
            const unnamed = group.name.trim() === "";
            return (
              <div
                key={group.id}
                className={cn(
                  "rounded-lg border border-zinc-200 p-4 dark:border-zinc-800",
                  dirty && "bg-amber-50 dark:bg-amber-900/20",
                )}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <input
                      type="text"
                      aria-label="Nom du groupement"
                      title={group.name}
                      value={group.name}
                      required
                      aria-invalid={unnamed}
                      onChange={(e) =>
                        onRename(group, { name: e.target.value })
                      }
                      className={cn(cellInputClass(dirty), "font-medium")}
                    />
                    {unnamed && (
                      <p className="px-2 text-xs text-red-600 dark:text-red-400">
                        Le nom du groupement est obligatoire.
                      </p>
                    )}
                    <input
                      type="text"
                      aria-label="Abréviation du groupement"
                      placeholder="Abréviation"
                      value={group.shortName ?? ""}
                      onChange={(e) =>
                        onRename(group, { shortName: e.target.value })
                      }
                      maxLength={50}
                      className={cn(cellInputClass(dirty), "text-xs")}
                    />
                  </div>
                  <Button
                    plain
                    onClick={() => onDelete(group)}
                    aria-label="Supprimer le groupement"
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
                <span className="mb-1 block text-xs font-medium text-zinc-500">
                  Mandataire
                </span>
                <Select
                  options={[
                    { label: "Aucun mandataire", value: "0" },
                    ...candidates.map((c) => ({
                      label: c.name,
                      value: String(c.id),
                    })),
                  ]}
                  value={[String(group.mandataireContractId ?? 0)]}
                  onValueChange={(details) => {
                    const val = details.value[0];
                    onSetMandataire(
                      group.id,
                      val && val !== "0" ? Number(val) : null,
                    );
                  }}
                  placeholder="Désigner le mandataire"
                  disabled={candidates.length === 0}
                />
                {candidates.length === 0 && (
                  <p className="mt-1 text-xs text-zinc-500">
                    Le mandataire se choisit parmi les contrats du groupement :
                    choisissez d'abord ce groupement dans la colonne Groupement
                    d'un contrat.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {isNotNil(note) && <p className="text-xs text-zinc-500">{note}</p>}
    </div>
  );
}

/**
 * Body of the save confirmation. It alerts on each saved contract changing
 * company that has a referent or team members: those employees of the
 * previous company are removed on save (screens 909 and 910).
 */
export function contractsSaveMessage(
  intro: string,
  contracts: {
    id: number;
    performer: { id: number; name: string };
    referent: object | null;
    teams: { members: object[] }[];
  }[],
  drafts: Map<number, { performerId: number }>,
): string {
  const warnings = contracts
    .filter((contract) => {
      const draft = drafts.get(contract.id);
      return (
        isNotNil(draft) &&
        draft.performerId !== contract.performer.id &&
        (isNotNil(contract.referent) ||
          contract.teams.some((team) => team.members.length > 0))
      );
    })
    .map(
      (contract) =>
        `Le contrat de ${contract.performer.name} change de société contractante : son référent et les membres de ses équipes, collaborateurs de ${contract.performer.name}, vont être retirés.`,
    );
  return [intro, ...warnings].join(" ");
}

export type ContractsPanelFrameProps = {
  /** Left part of the fixed top bar (the groupements toggle). */
  toolbar?: ReactNode;
  isDirty: boolean;
  isSaving: boolean;
  /** Checks the draft before the confirmation popup; shows its own message when it returns false. */
  canSave?: () => boolean;
  /** Body of the save confirmation popup. */
  saveMessage: string;
  onSave: () => void;
  onDiscard: () => void;
  children: ReactNode;
};

/**
 * Fixed top bar, always present, then the panel's sole scroll area. The
 * draft actions appear in that bar rather than above the tables, so nothing
 * below moves when editing starts or ends (screen-design.md), and the
 * tables' sticky titles pin at the top of the scroll area. Saving and
 * discarding the draft each ask for confirmation.
 */
export function ContractsPanelFrame({
  toolbar,
  isDirty,
  isSaving,
  canSave,
  saveMessage,
  onSave,
  onDiscard,
  children,
}: ContractsPanelFrameProps) {
  const [pendingConfirm, setPendingConfirm] = useState<
    "save" | "cancel" | null
  >(null);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex h-10 shrink-0 items-center gap-3">
        {toolbar}
        {isDirty && (
          <div className="ml-auto flex h-10 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 pr-0.5 pl-2 dark:border-amber-900 dark:bg-amber-900/20">
            <span className="text-sm text-amber-700 dark:text-amber-400">
              Modifications non enregistrées
            </span>
            <Button
              outline
              onClick={() => setPendingConfirm("cancel")}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (canSave?.() === false) return;
                setPendingConfirm("save");
              }}
              disabled={isSaving}
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={pendingConfirm === "save"}
        title="Enregistrer les modifications"
        confirmLabel="Enregistrer"
        cancelLabel="Revenir au tableau"
        onConfirm={() => {
          setPendingConfirm(null);
          onSave();
        }}
        onCancel={() => setPendingConfirm(null)}
      >
        {saveMessage}
      </ConfirmationDialog>

      <ConfirmationDialog
        open={pendingConfirm === "cancel"}
        title="Annuler les modifications"
        confirmLabel="Annuler les modifications"
        cancelLabel="Revenir au tableau"
        onConfirm={() => {
          setPendingConfirm(null);
          onDiscard();
        }}
        onCancel={() => setPendingConfirm(null)}
      >
        Toutes les modifications non enregistrées vont être perdues et les cases
        reprendront leur valeur initiale.
      </ConfirmationDialog>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-none">
        {children}
      </div>
    </div>
  );
}

export type ContractsSectionTitleProps = {
  title: string;
  /** Lines under the title (client of the contracts, yard state notes). */
  subtitle?: ReactNode;
  /** Right-aligned action, e.g. "Ajouter". */
  action?: ReactNode;
  /** Pins the title to the top of the scroll area (pass the hook's `titleRef`). */
  stickyRef?: Ref<HTMLDivElement>;
};

export function ContractsSectionTitle({
  title,
  subtitle,
  action,
  stickyRef,
}: ContractsSectionTitleProps) {
  return (
    <div
      ref={stickyRef}
      className={cn(
        "flex items-center justify-between gap-3 bg-white pb-3 dark:bg-zinc-950",
        isNotNil(stickyRef) && stickyTitleClass,
      )}
    >
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {title}
        </h3>
        {subtitle}
      </div>
      {action}
    </div>
  );
}

export type DeactivatedLabelProps = {
  title?: string;
};

/**
 * "Désactivé" status of a contract, referent or team member, written out:
 * the PO read the former check-mark picto as "active". It is not a button:
 * there is no reactivation feature (PO, 30/10).
 */
export function DeactivatedLabel({ title }: DeactivatedLabelProps) {
  return (
    <span
      className="shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
      title={title}
    >
      Désactivé
    </span>
  );
}

type EmployeeRef = {
  firstName: string | null;
  lastName: string | null;
} | null;

/** A referent replaced once the yard was commissioned (API: referentHistory). */
export type ReferentHistoryEntry = {
  id: number;
  employeeId: string;
  employee: EmployeeRef;
  replacedAt: string;
};

export const personName = (employeeId: string, employee: EmployeeRef) =>
  `${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.trim() ||
  employeeId;

/** Day a referent was replaced, as in « jusqu'au 30/10/2026 ». */
export const replacementDay = (replacedAt: string) =>
  new Date(replacedAt).toLocaleDateString("fr-FR");

export type FormerReferentProps = {
  name: string;
  /** « jusqu'au 30/10/2026 », or « Réf. jusqu'au … » among team members. */
  untilLabel: string;
  /** Full sentence on hover. */
  title: string;
  /** Shows the « x » that removes this line from the history. */
  onDelete?: () => void;
};

/**
 * A former referent on two lines: the name (truncated) with its « x », then
 * the date in full, which must stay readable in the narrow 909/910 columns
 * (PO, N28, 03/11).
 */
export function FormerReferent({
  name,
  untilLabel,
  title,
  onDelete,
}: FormerReferentProps) {
  return (
    <span
      className="flex w-full min-w-0 flex-col text-xs text-zinc-400"
      title={title}
    >
      <span className="flex min-w-0 items-center justify-between gap-1">
        <span className="min-w-0 truncate">{name}</span>
        {isNotNil(onDelete) && (
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Supprimer ${name} de l'historique des référents`}
            title="Supprimer de l'historique"
            className="shrink-0 rounded p-0.5 hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-zinc-800"
          >
            <XIcon className="size-3" />
          </button>
        )}
      </span>
      <span className="whitespace-nowrap">{untilLabel}</span>
    </span>
  );
}

export type ReferentCellProps = {
  referent: {
    employeeId: string;
    isActive: boolean;
    employee: EmployeeRef;
  } | null;
  /** Former referents, most recently replaced first. */
  history: ReferentHistoryEntry[];
  actions: ReactNode;
  onDeleteHistory?: (entry: ReferentHistoryEntry) => void;
};

/**
 * Referent column of screens 909/910: the current referent highlighted, then
 * the referents they replaced « jusqu'au <date> », most recent first (PO,
 * N74 30/10, N28 03/11).
 */
export function ReferentCell({
  referent,
  history,
  actions,
  onDeleteHistory,
}: ReferentCellProps) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <div className="flex w-full items-start justify-between gap-1">
        {isNil(referent) ? (
          <span className="text-zinc-400">Aucun</span>
        ) : (
          <span className="flex min-w-0 items-center gap-1">
            <span
              className={cn(
                "min-w-0 truncate rounded-full border px-1.5",
                referent.isActive
                  ? "border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300"
                  : "border-zinc-300 text-zinc-400 dark:border-zinc-600",
              )}
              title={personName(referent.employeeId, referent.employee)}
            >
              {personName(referent.employeeId, referent.employee)}
            </span>
            {!referent.isActive && (
              // Deactivated in the annuaire and not replaced yet.
              <DeactivatedLabel title="Désactivé : désignez un autre référent" />
            )}
          </span>
        )}
        <div className="flex shrink-0 items-center gap-1">{actions}</div>
      </div>
      {history.map((entry) => {
        const name = personName(entry.employeeId, entry.employee);
        const day = replacementDay(entry.replacedAt);
        return (
          <FormerReferent
            key={entry.id}
            name={name}
            untilLabel={`jusqu'au ${day}`}
            title={`${name}, référent du contrat jusqu'au ${day}`}
            onDelete={onDeleteHistory && (() => onDeleteHistory(entry))}
          />
        );
      })}
    </div>
  );
}

/** A former referent picked for removal from the history (PO, N28, 03/11). */
export type ReferentHistoryRemoval = {
  contractId: number;
  entry: ReferentHistoryEntry;
  /** The team they were the referent of; null for the contract referent. */
  teamName: string | null;
};

/** Binds a cell's « x » to its contract (and team) for the removal dialog. */
export const historyRemover = (
  onDeleteHistory: ((removal: ReferentHistoryRemoval) => void) | undefined,
  contractId: number,
  teamName: string | null = null,
) =>
  isNil(onDeleteHistory)
    ? undefined
    : (entry: ReferentHistoryEntry) =>
        onDeleteHistory({ contractId, entry, teamName });

export type ReferentHistoryDeleteDialogProps = {
  removal: ReferentHistoryRemoval | null;
  onClose: () => void;
  onDeleted: () => void;
};

/**
 * Confirms then removes a former referent from the history, e.g. after a
 * collaborator deactivated by mistake was switched back on. Shared by 909
 * and 910.
 */
export function ReferentHistoryDeleteDialog({
  removal,
  onClose,
  onDeleted,
}: ReferentHistoryDeleteDialogProps) {
  if (isNil(removal)) return null;
  const name = personName(removal.entry.employeeId, removal.entry.employee);
  const day = replacementDay(removal.entry.replacedAt);
  const message = isNil(removal.teamName)
    ? `Supprimer ${name}, référent du contrat jusqu'au ${day}, de l'historique des référents ? Cette suppression est définitive.`
    : `Supprimer ${name}, référent de l'équipe ${removal.teamName} jusqu'au ${day}, de l'historique des référents ? Cette suppression est définitive.`;

  const confirm = async () => {
    onClose();
    const { error } = await apiClient.DELETE(
      "/api/admin/contracts/{id}/referent-history/{entryId}",
      {
        params: {
          path: { id: removal.contractId, entryId: removal.entry.id },
        },
      },
    );
    if (isNotNil(error)) {
      toaster.error({
        title: "Suppression refusée",
        description: `${name} n'a pas été retiré de l'historique : ${error.error}`,
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: `${name} est retiré de l'historique des référents.`,
    });
    onDeleted();
  };

  return (
    <ConfirmationDialog
      open
      title="Supprimer de l'historique"
      confirmLabel="Supprimer"
      onConfirm={confirm}
      onCancel={onClose}
    >
      {message}
    </ConfirmationDialog>
  );
}
